<template>
  <div class="login auth-page" id="auth" :style="authStyle" v-if="!showLoader">
    <div class="container" v-if="company">
      <div class="row">
        <div class="col-12">
          <div class="card card-container card-container--wide mt-5">
            <div class="row mb-2 pb-2 border-bottom no-gutters" v-if="company && company.languages.length > 1">
              <div class="col-5 ml-auto mb-2 text-right">
                <select
                  class="border shadow-none"
                  name="language-select"
                  id="language-select"
                  v-model="language"
                  @change="updateLanguage"
                >
                  <option :value="null" disabled>
                    {{ 'settings.changeYourLanguage' | translate }}
                  </option>
                  <option v-for="lang in company.languages" :value="lang.localeCode" :key="lang.languageId">
                    {{ lang.languageName }}
                  </option>
                </select>
              </div>
            </div>
            <div class="row">
              <div class="col-12">
                <h2 class="my-4">{{ $t('verifyEmail.title') }}</h2>
                <div class="verify-subtitle row align-items-center py-3 border rounded no-gutters">
                  <div class="col-9 px-3">
                    {{ $t('verifyEmail.codeSubtitle') }}
                    <strong>{{ workEmail }}</strong>
                  </div>
                  <div class="col-3 text-right px-3">
                    <a @click.prevent="changeEmail" href="#" class="change-email-link">
                      {{ $t('verifyEmail.changeEmail') }}
                    </a>
                  </div>
                </div>
                <form class="form-signin mt-3" @submit.prevent="submitCode">
                  <input
                    type="text"
                    id="inputCode"
                    maxlength="5"
                    ref="codeInput"
                    class="form-control"
                    :class="{ 'is-invalid': codeError }"
                    :placeholder="$t('verifyEmail.codePlaceholder')"
                    required
                    autofocus
                    v-model="code"
                  />
                  <div v-if="codeError" class="invalid-feedback d-block">
                    {{ codeError }}
                  </div>

                  <br />
                  <primary-button :loading="loading" size="large" class="btn-block btn-signin" type="submit">
                    {{ $t('verifyEmail.verify') }}
                  </primary-button>
                  <div class="text-left mt-4">
                    {{ $t('verifyEmail.noMessageInfo') }}
                    <a @click.prevent="resendCode" href="#" class="resend-code-link">
                      {{ $t('verifyEmail.noMessageTryAgain') }}
                    </a>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="pt-24" v-else>
    <loader />
  </div>
</template>

<script>
import authMixin from '../../mixins/auth';
import { mapGetters } from 'vuex';
import { updateLanguage } from '@shared/translations';

export default {
  mixins: [authMixin],
  data() {
    return {
      code: '',
      codeError: null,
      loading: false,
      language: null,
      showLoader: false,
      workEmail: this.$route.query.workEmail || null
    };
  },
  computed: {
    ...mapGetters('settings', ['company', 'style']),
    authStyle() {
      if (this.style?.loginBackground?.url) {
        return {
          'background-image': `url(${this.style.loginBackground.url}) !important`,
          'background-size': 'cover !important',
          'background-position': 'center center',
          'background-repeat': 'no-repeat',
          'background-attachment': 'fixed'
        };
      }
      return {};
    },
    companyId() {
      return this.company?.id;
    }
  },
  mounted() {
    if (!this.workEmail || !this.companyId) {
      this.$router.replace({ name: 'verifyEmail' });
    }

    const localUser = window.localStorage.getItem('onboarding_user');
    if (localUser) {
      const parsed = JSON.parse(localUser);
      this.language = parsed.language || null;
    }

    this.$nextTick(() => {
      this.$refs.codeInput?.focus();
    });
  },
  methods: {
    updateLanguage() {
      const lang = this.company.languages.find((l) => l.localeCode === this.language);
      if (!lang) return;

      const user = {
        language: this.language,
        languageId: lang.languageId
      };

      window.localStorage.setItem('onboarding_user', JSON.stringify(user));
      window.localStorage.setItem('onboarding_defaultLanguage', this.language);
      updateLanguage(this.language);
    },
    changeEmail() {
      this.$router.push({
        name: 'verifyEmail',
        query: {
          workEmail: this.workEmail
        }
      });
    },
    resendCode() {
      this.loading = true;
      this.$http
        .post(`/v2/companies/${this.companyId}/users/emails/verification-requests`, {
          workEmail: this.workEmail
        })
        .then(() => {
          this.showSuccessNotification(this.$t('verifyEmail.newCodeWasSent'));
        })
        .catch((error) => {
          const errors = error?.response?.data?.errors || ['time_limit_not_passed'];
          let errorMessage = '';

          if (errors.includes('time_limit_not_passed')) {
            errorMessage = this.$t('verifyEmail.timeLimitError');
          } else if (errors.includes('unable_to_verify_email')) {
            errorMessage = this.$t('verifyEmail.domainError');
          } else {
            errorMessage = this.$t('notifications.error');
          }

          this.showErrorNotification(errorMessage);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitCode() {
      this.loading = true;
      this.$http
        .put(`/v2/companies/${this.companyId}/users/emails/verification-requests`, {
          workEmail: this.workEmail,
          code: this.code
        })
        .then(() => {
          this.$notify({
            type: 'success',
            message: this.$t('verifyEmail.verified'),
            position: 'bottom-right'
          });
          this.$router.push({ name: 'login' });
        })
        .catch((error) => {
          const errors = error?.response?.data?.errors || [];
          if (errors.includes('incorrect_verification_code')) {
            this.codeError = this.$t('verifyEmail.invalidCode');
          } else {
            this.showErrorNotification(this.$t('notifications.error'));
          }
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
@import './auth.scss';
</style>
